import React, { useState, useMemo } from 'react'
import Container from '@mui/material/Container'
import Typography from '@mui/material/Typography'

import { useStyles } from './styles'
import { CommonAccordion, IAccordionData } from '../../shared/components/common-accordion'
import { CommonButton } from '../../shared/components/common-button'

export const FAQ: React.FC = () => {
  const classes = useStyles()
  const [expanded, setExpanded] = useState(true)

  const data: IAccordionData[] = useMemo(
    () => [
      {
        title: 'Who can use the FHB risk map?',
        details:
          'Anyone interested in knowing the FHB, FDK and DON risk levels in their area can use this map. Using weather data across the Prairies, it provides a quantitative estimate of expected risk levels. The risk level estimated for an area does not mean that every field in that area will experience such risk. However, there is a strong statistical probability that the risk level indicated on the map for any crop on any specific day will match field observations. Producers should conduct a field survey validation before making fungicide application decisions.'
      },
      {
        title: 'How do I use the FHB risk map?',
        details:
          'Steps on how the map works are provided on the homepage under “How it works” or by visiting the Map tab on the top right corner of the screen.'
      },
      {
        title: 'For what time period can I use the FHB risk map?',
        details:
          'The risk map is calculated for each day starting May 15 through to August 31. Risk cannot be forecast beyond the current date. The risk map uses weather data from the previous 2 weeks from more than 500 weather stations across the Prairies to calculate the risk level by location for a selected date.'
      },
      {
        title: 'Which provinces are supported?',
        details: 'Alberta, Saskatchewan and Manitoba'
      },
      {
        title: 'How accurate is the FHB risk map data?',
        details:
          'The models were developed using plot studies from 17 locations across the three Canadian Prairie provinces - 6 in Alberta, 6 in Manitoba and 5 in Saskatchewan, collecting over 2000 site-years of samples over a 6-year period (2019 to 2024). The models were validated using an independent dataset collected from over 400 producer fields across the Priaries with accuracies ranging from 61 to 93%.'
      },
      {
        title: 'Do I have to pay to use the FHB risk map?',
        details:
          'The viewer is free to use. Funding for the development of the initial risk models was provided through the Integrated Crop Agronomy Cluster Program (2018-2023) with several collaborating industry funders, part of the Canadian Agricultural Partnership, along with in-kind support from the AB, SK and MB agriculture departments. The research was transitioned to an operational disease risk mapping tool with funding from the Sustainable Canadian Agricultural Partnership (2023-2026), several industry funders and provincial agriculture departments. The on-going operation of the Prairie FHB risk mapping tool (2026-2028) is funded by the provincial producer cereal groups in AB, SK and MB along with in-kind support from the agriculture departments in each province. The list of funding partners and weather data providers is on the home page.'
      },
      {
        title: 'How is the FHB risk calculated?',
        details:
          'FHB risk is calculated and updated each day from May 15 through Aug 31. On a given day, hourly weather data from 1:00 a.m. through midnight at each weather station for the previous 14-day period are used in the FHB risk models. The models were developed for Western Canada to generate risk values at each weather station. The FHB risk is then mapped out using a 10 km x 10 km grid for the prairie agricultural region. In each grid cell, the FHB risk is determined using inverse distance weighting from the risk values for the nearest 12 weather stations that are within 50 km of the grid cell.'
      },
      {
        title: 'Do the FHB risk maps for a specific date change after they are calculated?',
        details:
          'There may be times when weather data are delayed in being transmitted and this can create gaps in the hourly weather data needed at each station. If a weather station has any gap in its hourly data over the past 14 days, the station is not used in the FHB risk calculation. On each day, the FHB risk mapping tool acquires weather data going back 3 days, then recalculates the risk values for each station on all 3 days and then re- creates the maps for those days. By going back 3 days, some of the gaps in the hourly weather data can be filled in and more weather stations will be available for the inverse distance weighting at each grid cell on the maps. Thus, an FHB risk map for a given date may change slightly over the following 3-day period during which some gaps in the hourly weather data may be filled in. The FHB risk maps for the current day are the ones that are most likely to change because the most current date is the one for which there is the greatest likelihood of experiencing missing hourly data.'
      },
      {
        title: 'What is the animation feature and how can it be used?',
        children: (
          <>
            <Typography variant={'body2'} component={'p'} className={classes.faqDetails}>
              There is a slider bar at the bottom of the map. Once a risk map has been created, a user can click the
              Play button (<img alt={'Play button'} width={20} src={'/images/icons/fhb-play.png'} />
              ), and the display will cycle through a time series of the same risk maps for the past week, so it is
              simple to see how the risk values have changed. By clicking the Stop button (
              <img alt={'Stop button'} width={20} src={'/images/icons/fhb-pause.png'} />
              ), the animation will pause at the map for the date on which the button was clicked. (Note, the export
              function works only with the original date selected and does not export any preceding maps shown in the
              animation. For example, if June 15 was selected to create a risk map, then the export function will export
              the June 15 map. If you are looking at the animation feature and click the stop function to see the map
              for June 12, the export function will still export the June 15 map because it was the map originally
              selected. To export the June 12 map, enter June 12 for the date in the lefthand panel and then click
              &quot;Show Results&quot;. Then the June 12 map can be exported.)
            </Typography>

            <Typography variant={'body2'} component={'p'} className={classes.faqDetails}>
              The animation feature is included to show the recent changes in the various FHB risk types. When the risk
              level has been high or very high for several days, it increases the likelihood that the weather conditions
              have favored the development of the specific risk type in the particular crop if it is at a vulnerable
              stage (i.e. flowering stage for wheat, head emergence stage for barley). Likewise, low or moderate risk
              levels for several days decrease the likelihood that the weather conditions have favored the specific risk
              type when the crop is at a vulnerable stage. This is just one more perspective to consider and producers
              should still perform a field validation before making fungicide application decisions, since conditions
              for individual fields within a grid cell can vary.
            </Typography>
          </>
        )
      },
      {
        title: 'Why are the FHB risk levels different for different crops and risk types on the same day?',
        children: (
          <>
            <Typography variant={'body2'} component={'p'} className={classes.faqDetails}>
              Each risk model for each crop and risk type was developed independently of the other risk models. In each
              case, the risk model selected for each crop and type of risk was based on its statistical accuracy as well
              as its simplicity (i.e. a model with 2 weather parameters was preferred over a model with similar accuracy
              but having 3 or more weather parameters). Therefore, the differences in the level of risk between risk
              types on a given day are completely a result of the variation in the different weather parameters for each
              risk model over the preceding period of time.
            </Typography>
            <Typography variant={'body2'} component={'p'} className={classes.faqDetails}>
              The weather parameters used in each type of risk model are somewhat different from each other (as of
              spring 2026):
            </Typography>
            <Typography variant={'body2'} component={'p'} className={classes.faqDetails}>
              <em>Spring wheat FHB</em> - mean daily maximum relative humidity over the previous 14 days and the number
              of hours with temp between 25 and 28 in the previous 14 days
            </Typography>
            <Typography variant={'body2'} component={'p'} className={classes.faqDetails}>
              <em>Spring wheat FDK</em> - mean daily relative humidity over the previous 10 days
            </Typography>
            <Typography variant={'body2'} component={'p'} className={classes.faqDetails}>
              <em>Winter wheat FHB</em> - mean daily minimum air temperature over the previous 7 days, mean daily air
              temperature over the previous 7 days and the number of hours with relative humidity over 80% over the
              previous 7 days
            </Typography>
            <Typography variant={'body2'} component={'p'} className={classes.faqDetails}>
              <em>Barley FHB</em> - mean daily rainfall over the previous 14 days, mean daily temperature over the
              previous 14 days
            </Typography>
            <Typography variant={'body2'} component={'p'} className={classes.faqDetails}>
              <em>Durum FHB</em> - number of hours with both temp between 15 and 30 and relative humidity over 80% in
              the previous 10 days
            </Typography>
            <Typography variant={'body2'} component={'p'} className={classes.faqDetails}>
              <em>Durum FDK</em> - mean daily maximum relative humidity over the previous 7 days and mean daily minimum
              relative humidity over the previous 7 days
            </Typography>
            <Typography variant={'body2'} component={'p'} className={classes.faqDetails}>
              <em>Durum DON</em> - number of hours with both temp between 15 and 30 and relative humidity over 80% in
              the previous 10 days
            </Typography>
            <Typography variant={'body2'} component={'p'} className={classes.faqDetails}>
              Across all crops and risk types, some type of relative humidity measurement preceding the flowering period
              is the most common and prevalent weather factor in the models. This is consistent with our epidemiological
              knowledge of the disease&#39;s life cycle. The barley FHB risk model is the only one that includes a
              rainfall parameter. Several of the risk models also include some type of air temperature parameter.
            </Typography>
            <Typography variant={'body2'} component={'p'} className={classes.faqDetails}>
              Additional weather data and disease observations (both in research plots and producer fields) are being
              collected each year and added to a growing database. A recent study reassessed and updated the models with
              new weather parameters and coefficients with the current (2026) versions shown above (
              <a href='http://hdl.handle.net/1993/39749'>http://hdl.handle.net/1993/39749</a>). The study also
              illustrated that increasing the number of observations included in the databases improved the models’
              statistical accuracy.
            </Typography>
          </>
        )
      },
      {
        title: 'Which risk type should I select for spraying decisions or tracking disease risk?',
        children: (
          <>
            <Typography variant={'body2'} component={'p'} className={classes.faqDetails}>
              The three risk types shown in the map (FHB, FDK, DON) reflect different aspects of FHB disease impact on
              the crops. The most relevant risk type to consult will depend on your spraying objective and priorities,
              such as protecting yield, managing marketing risk or maintaining grain quality.
            </Typography>
            <Typography variant={'body2'} component={'p'} className={classes.faqDetails}>
              Previous research (<a href='http://hdl.handle.net/1993/36855'>http://hdl.handle.net/1993/36855</a>) has
              shown that the level of the FHB index during the grain filling stage (FHB risk) is not highly correlated
              to the levels of either FDK or DON in the grain from the same field. Therefore, the risk for FHB during
              the filling stage can be substantially different from either the FDK or DON risk in the harvested grain.
            </Typography>
            <Typography variant={'body2'} component={'p'} className={classes.faqDetails}>
              FHB risk relates more to the potential for yield loss. It is based on the FHB index, which reflects the
              level of visible disease in the field during grain filling. It is based on the number of infected heads
              that show early bleaching and may become shriveled and lightweight in relation to the number of spikelets
              that remain green and healthy. The shriveled and lightweight kernels reduce overall yield.
            </Typography>
            <Typography variant={'body2'} component={'p'} className={classes.faqDetails}>
              FDK and DON are more closely associated with potential grain quality concerns.
            </Typography>
            <Typography variant={'body2'} component={'p'} className={classes.faqDetails}>
              Fusarium-damaged kernels (FDK) are typically chalky and lightweight, and it is an official grading factor
              used by the Canadian Grain Commission. FDK risk shown in this map estimates the probability of having
              Fusarium-damaged kernels that will exceed the maximum level permitted in the No. 1 grade for each crop. If
              FDK levels exceed allowable limits, grain will be downgraded.
            </Typography>
            <Typography variant={'body2'} component={'p'} className={classes.faqDetails}>
              DON is a common mycotoxin produced in FHB-infected cereals. Although DON is currently not an official
              grain grading factor, high concentrations of DON can lead to price discounts or rejected loads. It is
              important to note that grain may look healthy but still contain DON, so testing at grain maturity or
              harvest is recommended.
            </Typography>
          </>
        )
      },
      {
        title:
          'There is a long list of varieties that can be selected in the mapping tool. Were they all tested in the research used to create the mapping tool?',
        children: (
          <>
            <Typography variant={'body2'} component={'p'} className={classes.faqDetails}>
              No. It was not feasible to test all varieties during the research to develop the FHB risk mapping tool.
              However, all the varieties listed have been tested over many years to determine and categorize their
              genetic resistance to FHB infection. Each registered variety in Canada has its specific FHB resistance
              category listed in all of the provincial seed guides. So, rather than testing each individual variety, the
              FHB risk models for barley, winter wheat, and spring wheat in the mapping tool were developed using the
              FHB resistance categories determined in previous research. For the FHB risk mapping tool research, the
              resistance levels were grouped into 3 broad categories:
              <ol className={classes.faqList}>
                <li>Group 1) – varieties that are rated as resistant (R) or moderately resistant (MR);</li>
                <li>Group 2) – varieties that are rated as intermediate (I) resistance;</li>
                <li>Group 3) – varieties that are rated susceptible (S) or moderately susceptible (MS).</li>
              </ol>
            </Typography>

            <Typography variant={'body2'} component={'p'} className={classes.faqDetails}>
              For durum, the models were all developed using susceptible or moderately susceptible cultivars.
            </Typography>
            <Typography variant={'body2'} component={'p'} className={classes.faqDetails}>
              Therefore, the variety names shown in the list for each crop are used as a proxy to represent one of the 3
              resistance groupings. This was the protocol utilized during the model development and validation research.
              It is the same protocol utilized to produce the FHB risk maps, which indicate the FHB risk for the
              specific FHB resistance grouping, and does NOT imply that each specific variety has been directly tested
              in the research.
            </Typography>
          </>
        )
      }
    ],
    [classes]
  )

  return (
    <Container component={'section'} className={classes.wrapper}>
      <div className={classes.headerRow}>
        <Typography aria-level={1} variant='h3' component='h1'>
          Frequently Asked Questions
        </Typography>
        <div>
          <CommonButton onClick={() => setExpanded((value) => !value)}>
            {expanded ? 'Collapse' : 'Expand'} All
          </CommonButton>
        </div>
      </div>
      {data.map((item, index) => (
        <CommonAccordion capitalize={false} key={index} title={item.title} details={item.details} expanded={expanded}>
          {item.children}
        </CommonAccordion>
      ))}
    </Container>
  )
}
